import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

/**
 * Renders a cart cross-sell list by merging Shopify's per-product "complementary"
 * recommendations across every distinct product currently in the cart. Displayed as a
 * horizontal, swipeable carousel (scroll-snap) of vertical product cards, with prev/next
 * buttons for mouse/trackpad users.
 *
 * Chris Nelis-Rouse — 15 Aug 2026
 *
 * Update — 18 Aug 2026: was a stacked list of compact horizontal rows (16 Aug change) — back
 * to a carousel per a follow-up request, this time built from scratch as a real card-per-item
 * carousel rather than the original tall-card version, using scroll-snap + the theme's own
 * `<scroll-hint>` wrapper (see cart-recommendations.liquid) instead of a bespoke slider.
 *
 * Feature — 18 Aug 2026: backup recommendations. If Shopify has no per-product recommendations
 * for what's currently in the cart, this used to just hide the section entirely — now it falls
 * back to a merchant-chosen collection instead (see #getFallbackProducts()), so shoppers still
 * see some suggestions rather than nothing.
 */
class CartRecommendations extends HTMLElement {
  /**
   * Fix — 16 Aug 2026: this element renders with the `hidden` attribute so the list
   * doesn't flash empty before data loads. `hidden` means `display: none`, which means the
   * element never gets a layout box — so an IntersectionObserver-based lazy-load (the
   * pattern product-recommendations.js uses, copied here originally) can NEVER fire: a
   * `display: none` element can never register as intersecting. That was silently blocking
   * every fetch. Load is now driven by the cart drawer's own open state/event instead, which
   * doesn't care whether this element is hidden.
   */

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName !== 'data-product-ids') continue;
      this.#loadRecommendations();
      break;
    }
  });

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Record<string, string>} */
  #cache = {};

  /**
   * @param {Event} event
   */
  #handleDrawerOpen = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#loadRecommendations();
  };

  connectedCallback() {
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-ids'],
    });

    // theme-drawer.js dispatches 'theme-drawer:open' (bubbling) on the <theme-drawer
    // id="cart-drawer"> element whenever the drawer opens.
    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen);

    // Covers the case where this element is re-inserted (e.g. by a section morph after
    // add-to-cart) while the drawer is already open, so no fresh 'open' event will fire.
    const dialog = this.closest('dialog');
    if (dialog?.open) {
      this.#loadRecommendations();
    }

    // Fix — 19 Aug 2026 (Chris Nelis-Rouse): this used to also grab `.cart-recommendations__scroller`
    // (the carousel's <scroll-hint> wrapper) and wire up prev/next nav buttons to scroll it —
    // both are gone now that this is a plain vertical list of row cards with no scroll of its
    // own (see cart-recommendations.liquid's markup comment). `this.list` is just where cards
    // get built now, nothing scrolls it directly any more.
    this.list = this.querySelector('.cart-recommendations__list');
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    document.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
    this.#activeFetch?.abort();
  }

  async #loadRecommendations() {
    const productIdsRaw = this.dataset.productIds;
    if (!productIdsRaw) {
      this.hidden = true;
      return;
    }

    const cartProductIds = productIdsRaw.split(',').filter(Boolean);
    const baseUrl = this.dataset.baseUrl;
    const intent = this.dataset.intent || 'complementary';
    const maxProducts = Number(this.dataset.maxProducts) || 6;

    if (!baseUrl || cartProductIds.length === 0) {
      this.hidden = true;
      return;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const results = await Promise.all(
        cartProductIds.map((id) => this.#fetchForProduct(baseUrl, id, intent, maxProducts, signal))
      );

      /** @type {Map<number, object>} */
      const merged = new Map();
      const excluded = new Set(cartProductIds.map(Number));

      for (const products of results) {
        for (const product of products) {
          if (excluded.has(product.id) || merged.has(product.id)) continue;
          merged.set(product.id, product);
          if (merged.size >= maxProducts) break;
        }
        if (merged.size >= maxProducts) break;
      }

      const products = Array.from(merged.values());

      if (products.length === 0) {
        // Feature — 18 Aug 2026 (Chris Nelis-Rouse): backup recommendations. Previously this
        // just hid the whole section whenever Shopify had nothing to recommend for what's in
        // the cart — flagged early on as something to revisit once the visual redesign was
        // settled. #getFallbackProducts() reads the merchant's chosen backup collection
        // (rendered server-side in cart-recommendations.liquid), already shaped like this
        // function's own `products` array above, so it can go straight into the same #render().
        const fallbackProducts = this.#getFallbackProducts(excluded, maxProducts);

        if (fallbackProducts.length === 0) {
          this.hidden = true;
          return;
        }

        this.#render(fallbackProducts);
        this.hidden = false;
        return;
      }

      this.#render(products);
      this.hidden = false;
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Cart recommendations error:', error);
      this.hidden = true;
    }
  }

  /**
   * @param {string} baseUrl
   * @param {string} productId
   * @param {string} intent
   * @param {number} limit
   * @param {AbortSignal} signal
   * @returns {Promise<object[]>}
   */
  async #fetchForProduct(baseUrl, productId, intent, limit, signal) {
    const url = `${baseUrl}?product_id=${productId}&intent=${intent}&limit=${limit}`;

    if (this.#cache[url]) return this.#cache[url];

    const response = await fetch(url, { signal });
    if (!response.ok) return [];

    const data = await response.json();
    const products = data?.products || [];
    this.#cache[url] = products;
    return products;
  }

  /**
   * Feature — 18 Aug 2026 (Chris Nelis-Rouse): reads the backup collection's products, rendered
   * server-side into `data-fallback-products` by cart-recommendations.liquid (empty array "[]"
   * if the merchant hasn't chosen a backup collection in Theme Settings). Only ever called when
   * the real per-product recommendations come back completely empty — see
   * #loadRecommendations() above. Filters out anything already in the cart, the same as the
   * real recommendations path does, so a backup product that happens to already be in the
   * basket doesn't show up as a "recommendation".
   *
   * @param {Set<number>} excludedIds - product ids already in the cart
   * @param {number} maxProducts
   * @returns {object[]}
   */
  #getFallbackProducts(excludedIds, maxProducts) {
    const raw = this.dataset.fallbackProducts;
    if (!raw) return [];

    try {
      const products = JSON.parse(raw);
      if (!Array.isArray(products)) return [];
      return products.filter((product) => !excludedIds.has(product.id)).slice(0, maxProducts);
    } catch (error) {
      console.error('Cart recommendations: could not parse backup products', error);
      return [];
    }
  }

  /**
   * Renders each recommendation as a horizontal row card: a small square thumbnail on the
   * left, title/price/variant-picker in the middle, "Add" button pinned to the right. Cards
   * stack vertically in a plain list — see the 19 Aug 2026 update note at the top of this file
   * for why this replaced the earlier horizontal-scroll carousel of square cards.
   *
   * @param {object[]} products
   */
  #render(products) {
    if (!this.list) return;

    const currency = this.dataset.currency;
    const addText = this.dataset.addText || 'Add';
    const addingText = this.dataset.addingText || 'Adding...';
    const addedText = this.dataset.addedText || 'Added';
    const errorText = this.dataset.errorText || 'Error adding to cart';
    const soldOutText = this.dataset.soldOutText || 'Sold out';

    this.list.innerHTML = '';

    for (const product of products) {
      const li = document.createElement('li');
      li.className = 'cart-recommendations-card';

      const variants = Array.isArray(product.variants) && product.variants.length ? product.variants : [];
      const firstAvailable = variants.find((variant) => variant.available) || variants[0];
      const productImage = product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);

      if (!firstAvailable) {
        li.classList.add('cart-recommendations-card--sold-out');
      }

      // --- thumbnail (left) ---
      const mediaLink = document.createElement('a');
      mediaLink.className = 'cart-recommendations-card__media';
      mediaLink.href = product.url;

      /** @type {HTMLImageElement | null} */
      let img = null;
      const initialImage = this.#variantImage(firstAvailable) || productImage;
      if (initialImage) {
        img = document.createElement('img');
        img.src = initialImage;
        img.alt = product.title;
        img.loading = 'lazy';
        mediaLink.append(img);
      }
      li.append(mediaLink);

      // --- body (middle): title on its own line, then a row with variant-select + price ---
      const body = document.createElement('div');
      body.className = 'cart-recommendations-card__body';

      const title = document.createElement('p');
      title.className = 'cart-recommendations-card__title';
      title.textContent = product.title;
      body.append(title);

      const meta = document.createElement('div');
      meta.className = 'cart-recommendations-card__meta';
      body.append(meta);

      const priceEl = document.createElement('span');
      priceEl.className = 'cart-recommendations-card__price';
      priceEl.textContent = this.#formatMoney(firstAvailable?.price ?? product.price, currency);

      /** @type {HTMLSelectElement | null} */
      let variantSelect = null;
      if (variants.length > 1) {
        variantSelect = document.createElement('select');
        variantSelect.className = 'cart-recommendations-card__variant-select';
        variantSelect.setAttribute('aria-label', product.title);
        for (const variant of variants) {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title;
          option.disabled = !variant.available;
          option.dataset.price = String(variant.price);
          if (variant.id === firstAvailable?.id) option.selected = true;
          variantSelect.append(option);
        }
        meta.append(variantSelect);
      }
      meta.append(priceEl);

      // --- add button (right) — sibling of the body, not nested inside it, so it stays
      // vertically centred against the whole row rather than stacked under the price. ---
      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'cart-recommendations-card__add';
      addButton.textContent = firstAvailable ? `+ ${addText}` : soldOutText;
      addButton.disabled = !firstAvailable;

      li.append(body, addButton);
      this.list.append(li);

      variantSelect?.addEventListener('change', () => {
        const option = variantSelect.selectedOptions[0];
        priceEl.textContent = this.#formatMoney(Number(option.dataset.price), currency);
        addButton.dataset.variantId = option.value;

        // Fix/feature — 16 Aug 2026: swap the thumbnail to match the selected variant, the
        // same way the main product page does, instead of leaving the first variant's image
        // showing regardless of what's selected.
        const selectedVariant = variants.find((variant) => String(variant.id) === option.value);
        const newImageSrc = this.#variantImage(selectedVariant) || productImage;
        if (newImageSrc) {
          if (!img) {
            img = document.createElement('img');
            img.alt = product.title;
            img.loading = 'lazy';
            mediaLink.append(img);
          }
          img.src = newImageSrc;
        }
      });

      addButton.dataset.variantId = String(firstAvailable?.id ?? '');
      addButton.addEventListener('click', () =>
        this.#addToCart(addButton, { addingText, addedText, errorText })
      );
    }
  }

  /**
   * Returns a variant's own image URL if it has one assigned, else null so callers can fall
   * back to the product's featured image. Handles both shapes the recommendations endpoint
   * can return a variant image in: a plain URL string, or a Shopify image object with `.src`.
   *
   * @param {object | undefined} variant
   * @returns {string | null}
   */
  #variantImage(variant) {
    const featuredImage = variant?.featured_image;
    if (!featuredImage) return null;
    if (typeof featuredImage === 'string') return featuredImage;
    return featuredImage.src || null;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {{addingText: string, addedText: string, errorText: string}} labels
   */
  async #addToCart(button, { addingText, addedText, errorText }) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = addingText;

    // Mirrors the optimistic dispatch/resolve pattern used by product-form.js and
    // component-cart-items.js, so header cart-count bubbles etc. update consistently.
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: CART_DRAWER_SECTION_ID,
          }),
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        const error = new Error(result.message || 'Add to cart failed');
        deferredEventPromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        button.textContent = errorText;
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2500);
        return;
      }

      button.textContent = addedText;

      const sectionHtml = result.sections?.[CART_DRAWER_SECTION_ID];
      if (sectionHtml) {
        morphSection(CART_DRAWER_SECTION_ID, sectionHtml, { mode: 'hydration', injectStylesheet: true });
      }

      // Fetch the full cart so CartLinesUpdateEvent listeners (cart bubble, etc.) get a
      // proper cart object, the same way product-form.js's #refreshCart() does.
      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const ajaxCart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-recommendations',
          sections: result.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error('Add to cart failed:', error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      button.textContent = errorText;
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2500);
    }
  }

  /**
   * @param {number | undefined} cents
   * @param {string | undefined} currency
   */
  #formatMoney(cents, currency) {
    if (typeof cents !== 'number') return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}
